import { Banner, Button, Spacer } from '@color/continuum';
import { UserRole, hasRole, useAuthContext } from '@color/lib';

import { IgvAlignmentStatus, useRequestSampleSequenceRunAlignmentRestoreMutation } from './api';

interface AlignmentStatusBannerProps {
  ssrId: string;
  status: IgvAlignmentStatus;
}

/**
 * Banner shown above the IGV browser when a run's alignment file cannot be
 * loaded as reads. Archived files are in Glacier and lab staff get an
 * "Unarchive" action that requests a restore (status then becomes `restoring`);
 * `unavailable` files are missing entirely. Nothing renders when the alignment
 * is `available`.
 */
export const AlignmentStatusBanner = ({ ssrId, status }: AlignmentStatusBannerProps) => {
  const { currentUser } = useAuthContext();
  const isLabStaff = currentUser != null && hasRole(currentUser, UserRole.LAB_STAFF);
  const restoreMutation = useRequestSampleSequenceRunAlignmentRestoreMutation(ssrId);

  if (status === 'available') {
    return null;
  }

  if (status === 'unavailable') {
    return (
      <Banner severity="warning" variant="in-component">
        No alignment file is available for this sample, so reads are not shown.
      </Banner>
    );
  }

  if (status === 'restoring') {
    return (
      <Banner severity="info" variant="in-component">
        Alignment files for this sample are being restored from archive. This typically takes 3–12
        hours, after which the reads will load here.
      </Banner>
    );
  }

  return (
    <Banner variant="in-component" severity="warning" action={null}>
      Alignment files for this sample have been archived, so reads are not shown.
      {isLabStaff && (
        <>
          <Spacer variant="small" />
          <Button
            size="small"
            color="warning"
            variant="outlined"
            loading={restoreMutation.isPending}
            onClick={() => restoreMutation.mutate()}
          >
            Unarchive
          </Button>
        </>
      )}
    </Banner>
  );
};
